import { Fragment, memo, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useSimUsageMonthly } from "@/hooks/useSims";
import {
  PROVIDER_LABELS,
  computeDeviceUsage,
  formatDataMb,
  shortIccid,
  type SimCard,
  type SimProvider,
} from "@/lib/sims";
import { ProviderBadge } from "./SimBadges";

/** Usage ranking by device (this month, last month, 12 months). */
export const SimUsageOverview = memo(function SimUsageOverview({
  sims,
  onOpenDevice,
}: {
  sims: SimCard[];
  onOpenDevice: (device: { id: string; name: string }) => void;
}) {
  const [provider, setProvider] = useState<SimProvider | "all">("all");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [since] = useState(() => {
    const now = new Date();
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 12, 1);
  });
  const { data: monthly } = useSimUsageMonthly(since);

  const rows = useMemo(
    () =>
      computeDeviceUsage(
        sims.filter((s) => provider === "all" || s.provider === provider),
        monthly,
      ),
    [sims, monthly, provider],
  );

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="space-y-3" data-testid="sim-usage-overview">
      <div className="flex items-center gap-2">
        <Select
          value={provider}
          onValueChange={(v) => setProvider(v as SimProvider | "all")}
        >
          <SelectTrigger className="w-44" data-testid="usage-provider-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All providers</SelectItem>
            {(Object.keys(PROVIDER_LABELS) as SimProvider[]).map((p) => (
              <SelectItem key={p} value={p}>
                {PROVIDER_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          This month uses the current billing cycle; history comes from the
          daily usage history sync.
        </span>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Device</TableHead>
              <TableHead className="text-right">SIMs</TableHead>
              <TableHead className="text-right">This month</TableHead>
              <TableHead className="text-right">Last month</TableHead>
              <TableHead className="text-right">12 months</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <Fragment key={row.key}>
                <TableRow
                  className="cursor-pointer"
                  onClick={() => toggle(row.key)}
                  data-testid={`usage-row-${row.deviceID ?? "unassigned"}`}
                >
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-1">
                      {expanded.has(row.key) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      {row.deviceID ? (
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenDevice({
                              id: row.deviceID!,
                              name: row.deviceName,
                            });
                          }}
                        >
                          {row.deviceName}
                        </button>
                      ) : (
                        <span className="text-muted-foreground">
                          Unassigned
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.simCount}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatDataMb(row.thisMonthMb)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatDataMb(row.lastMonthMb)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatDataMb(row.last12MonthsMb)}
                  </TableCell>
                </TableRow>
                {expanded.has(row.key) &&
                  row.sims.map(
                    ({ sim, thisMonthMb, lastMonthMb, last12MonthsMb }) => (
                      <TableRow key={sim.id} className="bg-muted/30 text-xs">
                        <TableCell className="pl-10">
                          <span className="inline-flex items-center gap-2">
                            <span className="font-mono" title={sim.iccid}>
                              {shortIccid(sim.iccid)}
                            </span>
                            <ProviderBadge provider={sim.provider} />
                            {sim.slot ? `Slot ${sim.slot}` : ""}
                          </span>
                        </TableCell>
                        <TableCell />
                        <TableCell className="text-right tabular-nums">
                          {formatDataMb(thisMonthMb)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatDataMb(lastMonthMb)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatDataMb(last12MonthsMb)}
                        </TableCell>
                      </TableRow>
                    ),
                  )}
              </Fragment>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="py-8 text-center text-muted-foreground"
                >
                  No SIMs.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
});
